import { useState } from "react";
import type { MeResponse } from "../../shared/api.js";
import type { Theme } from "../lib/theme.js";
import { api } from "../lib/api.js";
import { navigate } from "../lib/router.js";
import { Button } from "../components/Button.js";
import { Field } from "../components/Field.js";
import { ThemeToggle } from "../components/ThemeToggle.js";

const TIMEZONES = [
  "UTC",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Rome",
  "America/New_York",
  "America/Los_Angeles",
  "America/Toronto",
  "Australia/Sydney",
];

export function SettingsScreen({
  me,
  onUpdate,
  theme,
  setTheme,
}: {
  me: MeResponse;
  onUpdate: (m: MeResponse) => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
}) {
  const [displayName, setDisplayName] = useState(me.user.displayName);
  const [timezone, setTimezone] = useState(me.user.timezone);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.updateMe({ displayName: displayName.trim(), timezone });
      const next = await api.me();
      onUpdate(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  async function logoutAll() {
    if (!confirm("Sign out everywhere? This revokes all of your sessions.")) return;
    await api.logoutAll();
    navigate("/", { replace: true });
    window.location.reload();
  }

  async function logout() {
    await api.logout();
    navigate("/", { replace: true });
    window.location.reload();
  }

  return (
    <div className="flex flex-col gap-6">
      <section>
        <h1 className="font-display text-[28px] text-granite-900 dark:text-granite-50 mb-1">
          Settings
        </h1>
        <p className="font-text text-granite-600 dark:text-granite-300">
          {me.user.email}
        </p>
      </section>

      <form onSubmit={save} className="panel p-4 flex flex-col gap-4">
        <Field
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={60}
        />
        <div className="flex flex-col gap-1.5">
          <label htmlFor="timezone" className="font-text text-[15px] text-granite-700 dark:text-granite-200">
            Timezone
          </label>
          <select
            id="timezone"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className="min-h-[48px] px-3 rounded-[var(--radius-control)] border bg-white dark:bg-granite-900 border-granite-300 dark:border-granite-600 text-granite-800 dark:text-granite-100 text-[17px]"
          >
            {TIMEZONES.includes(timezone) ? null : <option value={timezone}>{timezone}</option>}
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="font-text text-blaze text-[15px]" role="alert">{error}</p>}
        {saved && <p className="font-text text-glacier text-[15px]" role="status">Saved.</p>}
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </form>

      <section className="panel p-4 flex flex-col gap-3">
        <h2 className="font-text font-semibold text-[17px] text-granite-800 dark:text-granite-100">
          Appearance
        </h2>
        <p className="font-text text-[15px] text-granite-600 dark:text-granite-300">
          Day is bright for outdoor use; Refuge is dark for bunkrooms.
        </p>
        <ThemeToggle theme={theme} onChange={setTheme} />
      </section>

      <section className="panel p-4 flex flex-col gap-3">
        <h2 className="font-text font-semibold text-[17px] text-granite-800 dark:text-granite-100">
          Session
        </h2>
        <div className="flex flex-col gap-2">
          <Button variant="secondary" onClick={logout}>
            Sign out
          </Button>
          <Button variant="danger" onClick={logoutAll}>
            Sign out everywhere
          </Button>
        </div>
      </section>
    </div>
  );
}
